import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, FileSignature } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ContactMethodPicker, { CONTACT_METHOD_LABEL } from "@/components/ContactMethodPicker";

const DURATIONS = [10, 15, 20, 25, 30];

export default function PolicySignDialog({ summary, triggerLabel = "Skleni si zavarovanje" }) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wantsCalc, setWantsCalc] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    contact_method: "email",
    note: "",
    age: "",
    height: "",
    weight: "",
    smoker: "no",
    duration: "20",
    occupation: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (form.contact_method === "email" && !form.email) {
      toast.error("Vnesite e-naslov.");
      return;
    }
    if ((form.contact_method === "phone" || form.contact_method === "whatsapp") && !form.phone) {
      toast.error("Vnesite telefonsko številko.");
      return;
    }
    if (wantsCalc) {
      if (!form.age || !form.height || !form.weight || !form.duration || !form.occupation) {
        toast.error("Za informativni izračun izpolnite vsa polja (starost, višina, teža, poklic, trajanje).");
        return;
      }
    }
    setLoading(true);
    try {
      const personalLines = wantsCalc
        ? [
            "",
            "Podatki za informativni izračun (brez posveta):",
            `• Starost: ${form.age} let`,
            `• Višina: ${form.height} cm`,
            `• Teža: ${form.weight} kg`,
            `• Kadilec: ${form.smoker === "yes" ? "DA" : "NE"}`,
            `• Poklic: ${form.occupation}`,
            `• Trajanje zavarovanja: ${form.duration} let`,
          ]
        : [];

      const message = [
        wantsCalc
          ? "Prošnja za informativni izračun (brez posveta):"
          : "Sklenitev zavarovanja iz kalkulatorja:",
        "",
        ...Object.entries(summary || {}).map(([k, v]) => `• ${k}: ${v}`),
        ...personalLines,
        "",
        form.note ? `Opomba stranke: ${form.note}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      await api.post("/inquiries", {
        name: form.name || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        contact_method: form.contact_method,
        topic: wantsCalc ? "Informativni izračun" : "Sklenitev zavarovanja",
        message,
      });
      setSubmitted(true);
      toast.success(
        wantsCalc
          ? "Prošnja za informativni izračun poslana."
          : "Prošnja za sklenitev je bila poslana."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({
      name: "", email: "", phone: "", contact_method: "email", note: "",
      age: "", height: "", weight: "", smoker: "no", duration: "20", occupation: "",
    });
    setWantsCalc(false);
    setSubmitted(false);
    setOpen(false);
  };

  return (
    <div className="w-full">
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSubmitted(false); }}>
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="sign-policy-trigger"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white text-[var(--vp-primary)] font-semibold px-6 py-3 hover:bg-white/90 transition-colors"
          >
            <FileSignature size={16} /> {triggerLabel}
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {submitted ? (
            <div className="text-center py-4" data-testid="sign-policy-submitted">
              <CheckCircle2 className="mx-auto text-[var(--vp-primary)]" size={44} />
              <h3 className="font-display text-2xl font-bold mt-3">Hvala!</h3>
              <p className="text-[var(--vp-muted)] mt-2 text-sm">
                Prošnjo sem prejel. Konkreten informativni izračun ali ponudbo prejmete v
                24 urah na vaš e-naslov.
              </p>
              <Button
                onClick={reset}
                data-testid="sign-policy-close"
                className="mt-5 rounded-full"
                style={{ background: "var(--vp-primary)", color: "white" }}
              >
                Zapri
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Skleni si zavarovanje</DialogTitle>
                <DialogDescription>
                  Že imate polico? Preverite možnosti o menjavi. Če je še nimate, lahko
                  dobite konkreten informativni izračun v 24 urah.
                </DialogDescription>
              </DialogHeader>

              {summary && Object.keys(summary).length > 0 && (
                <div className="rounded-xl border border-[var(--vp-border)] bg-[var(--vp-sand)]/40 p-3 text-xs space-y-1">
                  <div className="font-semibold uppercase tracking-wider text-[var(--vp-muted)] mb-1">
                    Povzetek izračuna
                  </div>
                  {Object.entries(summary).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3">
                      <span className="text-[var(--vp-muted)]">{k}</span>
                      <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold">Kako vas naj kontaktiram?</Label>
                <p className="text-xs text-[var(--vp-muted)] mt-0.5 mb-3">
                  Izbrano: {CONTACT_METHOD_LABEL[form.contact_method]}.
                </p>
                <ContactMethodPicker
                  value={form.contact_method}
                  onChange={(v) => setForm({ ...form, contact_method: v })}
                  testid="sign-method"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Ime in priimek</Label>
                  <Input
                    data-testid="sign-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Neobvezno"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">
                    Telefon{form.contact_method !== "email" ? " *" : ""}
                  </Label>
                  <Input
                    data-testid="sign-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required={form.contact_method !== "email"}
                    placeholder={form.contact_method !== "email" ? "Obvezno" : "Neobvezno"}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">
                  E-naslov{form.contact_method === "email" ? " *" : ""}
                </Label>
                <Input
                  data-testid="sign-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required={form.contact_method === "email"}
                  placeholder={form.contact_method === "email" ? "Obvezno" : "Neobvezno"}
                  className="mt-1.5"
                />
              </div>

              <label
                htmlFor="want-calc"
                className="flex items-start gap-3 rounded-xl border border-[var(--vp-border)] bg-[var(--vp-sand)]/40 p-3 cursor-pointer"
              >
                <Checkbox
                  id="want-calc"
                  data-testid="sign-want-calc"
                  checked={wantsCalc}
                  onCheckedChange={(v) => setWantsCalc(Boolean(v))}
                  className="mt-0.5"
                />
                <div className="text-sm">
                  <div className="font-semibold">Želim informativni izračun brez posveta</div>
                  <div className="text-xs text-[var(--vp-muted)] mt-0.5">
                    Pošljemo vam konkreten izračun na e-naslov v 24 urah.
                  </div>
                </div>
              </label>

              {wantsCalc && (
                <div
                  data-testid="sign-calc-fields"
                  className="space-y-4 rounded-xl border border-[var(--vp-primary)]/30 bg-white p-4"
                >
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Starost (let) *</Label>
                      <Input
                        data-testid="sign-age"
                        type="number"
                        min={1}
                        max={120}
                        value={form.age}
                        onChange={(e) => setForm({ ...form, age: e.target.value })}
                        required={wantsCalc}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Višina (cm) *</Label>
                      <Input
                        data-testid="sign-height"
                        type="number"
                        min={50}
                        max={250}
                        value={form.height}
                        onChange={(e) => setForm({ ...form, height: e.target.value })}
                        required={wantsCalc}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Teža (kg) *</Label>
                      <Input
                        data-testid="sign-weight"
                        type="number"
                        min={20}
                        max={300}
                        value={form.weight}
                        onChange={(e) => setForm({ ...form, weight: e.target.value })}
                        required={wantsCalc}
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Kadilec / nekadilec *</Label>
                    <RadioGroup
                      value={form.smoker}
                      onValueChange={(v) => setForm({ ...form, smoker: v })}
                      className="flex gap-4 mt-2"
                      data-testid="sign-smoker"
                    >
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <RadioGroupItem value="no" data-testid="sign-smoker-no" /> Nekadilec
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <RadioGroupItem value="yes" data-testid="sign-smoker-yes" /> Kadilec
                      </label>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Vrsta poklica *</Label>
                    <Input
                      data-testid="sign-occupation"
                      value={form.occupation}
                      onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                      placeholder="npr. monter, pisarniško delo, voznik…"
                      required={wantsCalc}
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Trajanje zavarovanja *</Label>
                    <Select
                      value={form.duration}
                      onValueChange={(v) => setForm({ ...form, duration: v })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="sign-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATIONS.map((d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d} let
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold">Opomba</Label>
                <Textarea
                  data-testid="sign-note"
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Posebne želje, vprašanja…"
                  className="mt-1.5"
                />
              </div>

              <DialogFooter>
                <Button
                  type="submit"
                  data-testid="sign-submit"
                  disabled={loading}
                  className="w-full sm:w-auto rounded-full px-7 py-5 font-semibold"
                  style={{ background: "var(--vp-primary)", color: "white" }}
                >
                  {loading ? "Pošiljanje…" : wantsCalc ? "Naroči informativni izračun" : "Pošlji prošnjo"}
                </Button>
              </DialogFooter>
              <p className="text-xs text-[var(--vp-muted)] text-center">
                Vaši podatki se uporabljajo izključno za pripravo ponudbe.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <p
        data-testid="sign-policy-note"
        className="text-xs text-center mt-2 opacity-75 leading-snug"
      >
        Že imate polico? Preverite možnosti o menjavi. Če je še nimate, lahko dobite
        konkreten informativni izračun v 24 urah.
      </p>
    </div>
  );
}
